import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "filters-row" }
const _hoisted_2 = { class: "btn" }

import { computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'

import { PaginationParams } from '@/models/api'
import { EvTrip } from '@/models/evTrip'

import { GetEvTripsParams, useEvTripsStore } from '@/store/evTrips'

import { formatDateWithTime } from '@/helpers/date'
import { commonCase } from '@/helpers/strings'
import { showToast } from '@/helpers/messages'
import { normalizeUserName } from '@/helpers/user'

import useQueryParams from '@/composables/useQueryParams'

import CommonError from '@/components/CommonError.vue'
import Loader from '@/components/Loader.vue'
import { Ref, ref } from 'vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'List',
  props: {
    query: {}
  },
  setup(__props: any) {

const props = __props

const evTripsStore = useEvTripsStore()

const router = useRouter()

const isLoading = computed(() => evTripsStore.listModule.isLoading)
const isInitialLoadingDone = computed(
  () => evTripsStore.listModule.isInitialLoadingDone
)
const error = computed(() => evTripsStore.listModule.error)
const list = computed(() => evTripsStore.listModule.list)
const meta = computed(() => evTripsStore.listModule.meta)

// A failed reload keeps the already loaded data and only shows a toast.
watch(error, e => {
  if (e && list.value.length) showToast({ type: 'error', message: e })
})

const searchTerm: Ref<string> = ref('')
const selectedStatuses: Ref<string[] | null> = ref(null)

const getEvTrips = (params: PaginationParams & GetEvTripsParams) => {
  evTripsStore.getEvTrips(params)
}

const params = useQueryParams<PaginationParams & GetEvTripsParams>({
  ...props.query,
})

const normalizeEvCarName = (evCarProfile: any) => {
  if (!evCarProfile) return 'N/A'
  return evCarProfile.plate_number || 'N/A'
}

const filterByStatus = (statuses: string[] | null) => {
  selectedStatuses.value = statuses
  // Update API params with statuses
  if (statuses) {
    params.statuses = statuses
  } else {
    delete params.statuses
  }
  // Reset to first page when filtering
  params.page = 1
}

const getTripTotalAmount = (trip: any) => {
  const totalAmount = Number(trip?.total_amount)
  const effectiveTotalAmount = Number(trip?.effective_total_amount)

  const breakdownTotalAmount = Number(
    trip?.rush_breakdown?.total_amount ??
      trip?.per_hour_breakdown?.total_amount ??
      trip?.per_minute_breakdown?.total_amount
  )

  if (
    Number.isFinite(totalAmount) &&
    Number.isFinite(effectiveTotalAmount) &&
    totalAmount === effectiveTotalAmount &&
    Number.isFinite(breakdownTotalAmount)
  ) {
    return breakdownTotalAmount
  }

  if (Number.isFinite(totalAmount)) {
    return totalAmount
  }

  if (Number.isFinite(breakdownTotalAmount)) {
    return breakdownTotalAmount
  }

  return 0
}

const filteredTripsList = computed(() => {
  let filteredList = list.value

  if (searchTerm.value) {
    const lowerCaseSearchTerms = searchTerm.value.toLowerCase().split(' ')
    filteredList = filteredList.filter((trip: any) => {
      const hostFirstName = trip.host?.first_name?.toLowerCase() || ''
      const hostLastName = trip.host?.last_name?.toLowerCase() || ''
      const renterFirstName = trip.renter?.first_name?.toLowerCase() || ''
      const renterLastName = trip.renter?.last_name?.toLowerCase() || ''
      return lowerCaseSearchTerms.every(
        term =>
          hostFirstName.startsWith(term) ||
          hostLastName.startsWith(term) ||
          renterFirstName.startsWith(term) ||
          renterLastName.startsWith(term)
      )
    })
  }

  return filteredList
})

onMounted(() => {
  if (params.statuses) {
    selectedStatuses.value = params.statuses
  }
  getEvTrips(params)
})

watch(
  () => props.query,
  updated => {
    if (updated.statuses) {
      selectedStatuses.value = updated.statuses as string[]
    } else {
      selectedStatuses.value = null
    }
    Object.assign(params, updated)
  },
  { deep: true }
)

watch(params, updatedParams => {
  getEvTrips(updatedParams)
})

watch(selectedStatuses, updatedStatuses => {
  if (updatedStatuses) {
    params.statuses = updatedStatuses
  } else {
    delete params.statuses
  }
})

const onRetryPress = () => {
  getEvTrips(params)
}

const handleRowClick = (trip: EvTrip) => {
  router.push(`/ev-trips/${trip.id}`)
}

const getRowClassName = ({ row }: { row: any }) => {
  if (row.status === 'ongoing') return 'ongoing-trip-row'
  if (row.status === 'finished' || row.status === 'completed')
    return 'finished-trip-row'
  if (row.status === 'pending') return 'pending-trip-row'
  return ''
}

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_scrollbar = _resolveComponent("el-scrollbar")!
  const _directive_loading = _resolveDirective("loading")!

  return (error.value && !list.value.length)
    ? (_openBlock(), _createBlock(CommonError, {
        key: 0,
        errorMessage: error.value,
        onRetryPress: onRetryPress
      }, null, 8, ["errorMessage"]))
    : (!isInitialLoadingDone.value || !meta.value)
      ? (_openBlock(), _createBlock(Loader, { key: 1 }))
      : (_openBlock(), _createBlock(_component_el_scrollbar, {
          key: 2,
          always: ""
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", null, [
              _createVNode(_component_el_input, {
                modelValue: searchTerm.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchTerm).value = $event)),
                placeholder: "Search by first name or last name",
                class: "input-margin"
              }, null, 8, ["modelValue"]),
              _cache[14] || (_cache[14] = _createElementVNode("br", null, null, -1)),
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_el_button, {
                    type: !selectedStatuses.value ? 'primary' : 'default',
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (filterByStatus(null)))
                  }, {
                    default: _withCtx(() => _cache[8] || (_cache[8] = [
                      _createTextVNode(" All trips ")
                    ])),
                    _: 1,
                    __: [8]
                  }, 8, ["type"]),
                  _createVNode(_component_el_button, {
                    type: 
              selectedStatuses.value?.includes('pending') ? 'primary' : 'default'
            ,
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (filterByStatus(['pending'])))
                  }, {
                    default: _withCtx(() => _cache[9] || (_cache[9] = [
                      _createTextVNode(" Pending ")
                    ])),
                    _: 1,
                    __: [9]
                  }, 8, ["type"]),
                  _createVNode(_component_el_button, {
                    type: 
              selectedStatuses.value?.includes('ongoing') ? 'primary' : 'default'
            ,
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (filterByStatus(['ongoing'])))
                  }, {
                    default: _withCtx(() => _cache[10] || (_cache[10] = [
                      _createTextVNode(" Ongoing ")
                    ])),
                    _: 1,
                    __: [10]
                  }, 8, ["type"]),
                  _createVNode(_component_el_button, {
                    type: 
              selectedStatuses.value?.includes('finished') ? 'primary' : 'default'
            ,
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (filterByStatus(['finished'])))
                  }, {
                    default: _withCtx(() => _cache[11] || (_cache[11] = [
                      _createTextVNode(" Finished ")
                    ])),
                    _: 1,
                    __: [11]
                  }, 8, ["type"]),
                  _createVNode(_component_el_button, {
                    type: 
              selectedStatuses.value?.includes('on_hold') ? 'primary' : 'default'
            ,
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (filterByStatus(['on_hold'])))
                  }, {
                    default: _withCtx(() => _cache[12] || (_cache[12] = [
                      _createTextVNode(" On Hold ")
                    ])),
                    _: 1,
                    __: [12]
                  }, 8, ["type"])
                ]),
                _cache[13] || (_cache[13] = _createElementVNode("div", { class: "color-legend" }, [
                  _createElementVNode("div", { class: "legend-item" }, [
                    _createElementVNode("span", { class: "legend-dot ongoing" }),
                    _createElementVNode("span", { class: "legend-label" }, "Ongoing")
                  ]),
                  _createElementVNode("div", { class: "legend-item" }, [
                    _createElementVNode("span", { class: "legend-dot finished" }),
                    _createElementVNode("span", { class: "legend-label" }, "Finished")
                  ]),
                  _createElementVNode("div", { class: "legend-item" }, [
                    _createElementVNode("span", { class: "legend-dot pending" }),
                    _createElementVNode("span", { class: "legend-label" }, "Pending")
                  ])
                ], -1))
              ])
            ]),
            _createVNode(_component_el_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_el_col, { span: 24 }, {
                  default: _withCtx(() => [
                    _withDirectives((_openBlock(), _createBlock(_component_el_table, {
                      style: {"width":"100%"},
                      data: filteredTripsList.value,
                      onRowClick: handleRowClick,
                      "element-loading-background": "rgba(255, 255, 255, 0.5)",
                      class: "ev-trips-table",
                      "row-class-name": getRowClassName
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_table_column, {
                          prop: "id",
                          label: "Id"
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "status",
                          label: "Status"
                        }, {
                          default: _withCtx((scope) => [
                            _createTextVNode(_toDisplayString(_unref(commonCase)(scope.row.status)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, { label: "Vehicle info" }, {
                          default: _withCtx((scope) => [
                            _createTextVNode(_toDisplayString(normalizeEvCarName(scope.row.ev_car_profile)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, { label: "Renter" }, {
                          default: _withCtx((scope) => [
                            _createTextVNode(_toDisplayString(_unref(normalizeUserName)(scope.row.renter)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "start_date",
                          label: "Start date"
                        }, {
                          default: _withCtx(({ row }) => [
                            _createTextVNode(_toDisplayString(_unref(formatDateWithTime)(row.start_date)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "end_date",
                          label: "End date"
                        }, {
                          default: _withCtx(({ row }) => [
                            _createTextVNode(_toDisplayString(_unref(formatDateWithTime)(row.end_date)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, {
                          prop: "total_amount",
                          label: "Total amount"
                        }, {
                          default: _withCtx(({ row }) => [
                            _createTextVNode(_toDisplayString(getTripTotalAmount(row).toFixed(2)), 1)
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_el_table_column, {
                          width: "130px",
                          align: "right"
                        }, {
                          default: _withCtx((scope) => [
                            _createVNode(_component_el_button, {
                              plain: "",
                              type: "primary",
                              size: "small",
                              class: "test-btn",
                              icon: "el-icon-view",
                              onClick: ($event: any) => (handleRowClick(scope.row))
                            }, {
                              default: _withCtx(() => _cache[15] || (_cache[15] = [
                                _createTextVNode(" Show ")
                              ])),
                              _: 2,
                              __: [15]
                            }, 1032, ["onClick"])
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["data"])), [
                      [_directive_loading, isLoading.value]
                    ]),
                    _createVNode(_component_el_pagination, {
                      background: "",
                      layout: "total, sizes, prev, pager, next",
                      total: meta.value.total,
                      disabled: isLoading.value,
                      "page-sizes": [10, 20, 50, 100],
                      "page-size": _unref(params).perPage,
                      "onUpdate:pageSize": _cache[6] || (_cache[6] = ($event: any) => ((_unref(params).perPage) = $event)),
                      "current-page": _unref(params).page,
                      "onUpdate:currentPage": _cache[7] || (_cache[7] = ($event: any) => ((_unref(params).page) = $event)),
                      "popper-class": "ev-pagination-popper",
                      class: "el-pagination"
                    }, null, 8, ["total", "disabled", "page-size", "current-page"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
}
}

})